import React, { useEffect, useMemo, useState } from 'react';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import { Plus, Trash2, Info, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { accountingAPI, agencyAPI, agentAPI, carrierAPI } from '@/lib/api';

/**
 * ManualPaymentDialog — Phase 27.10 (Fase A UI + Fase B backend wire-up).
 *
 * Records a payment that lives OUTSIDE any carrier statement (bonuses,
 * reimbursements, corrections, one-off commission entries). Header +
 * lines mirror the shape of an imported statement so the record flows
 * through the same Payment Center → Batch → paid pipeline as a real
 * import.
 *
 * Persists to POST /accounting/carrier-imports/manual which creates a
 * carrier_imports row (statement_source="manual") + carrier_import_lines
 * with the recipient pre-confirmed and every field the reconciliation
 * engine expects already filled in.
 */
const PAYMENT_TYPES = [
  { value: 'commission',    label: 'Commission',    needsCarrier: true,  needsProduct: true,  needsMember: true },
  { value: 'bonus',         label: 'Bonus',         needsCarrier: false, needsProduct: false, needsMember: false },
  { value: 'reimbursement', label: 'Reimbursement', needsCarrier: false, needsProduct: false, needsMember: false },
  { value: 'correction',    label: 'Correction',    needsCarrier: false, needsProduct: false, needsMember: false },
  { value: 'other',         label: 'Other',         needsCarrier: false, needsProduct: false, needsMember: false },
];

const RECIPIENT_TYPES = [
  { value: 'agent',     label: 'Agent' },
  { value: 'agency',    label: 'Agency' },
  { value: 'subagency', label: 'Sub-Agency' },
  { value: 'manager',   label: 'Manager' },
];

const now = new Date();
const emptyLine = () => ({
  carrier_id: '',
  product: '',
  sub_type: 'New',        // New | Renewal (only for commission)
  member_name: '',
  effective_date: '',
  description: '',
  amount: '',
});

export const ManualPaymentDialog = ({ open, onOpenChange, onSaved }) => {
  const [header, setHeader] = useState({
    recipient_type: 'agent',
    recipient_id: '',
    payment_type: 'commission',
    period_month: now.getMonth() + 1,
    period_year: now.getFullYear(),
    notes: '',
  });
  const [lines, setLines] = useState([emptyLine()]);
  const [recipientOptions, setRecipientOptions] = useState([]);
  const [carriers, setCarriers] = useState([]);
  const [saving, setSaving] = useState(false);

  const typeMeta = PAYMENT_TYPES.find((t) => t.value === header.payment_type);

  // Load recipient list when the recipient_type changes so the search
  // stays scoped to the correct catalog.
  useEffect(() => {
    let cancelled = false;
    async function load() {
      try {
        if (header.recipient_type === 'agent' || header.recipient_type === 'manager') {
          const r = await agentAPI.list();
          if (!cancelled) setRecipientOptions((r.data || []).map((a) => ({
            id: a.npn, label: `${a.first_name || ''} ${a.last_name || ''} · NPN ${a.npn}`.trim(),
          })));
        } else if (header.recipient_type === 'agency' || header.recipient_type === 'subagency') {
          const r = await agencyAPI.list();
          const items = r.data?.items || r.data || [];
          if (!cancelled) setRecipientOptions(items.map((a) => ({
            id: a.agency_id, label: a.agency_name,
          })));
        }
      } catch { if (!cancelled) setRecipientOptions([]); }
    }
    if (open) load();
    return () => { cancelled = true; };
  }, [open, header.recipient_type]);

  // Carrier catalog for commission lines.
  useEffect(() => {
    if (!open) return;
    carrierAPI.list().then((r) => setCarriers((r.data || []).filter((c) => c.active))).catch(() => setCarriers([]));
  }, [open]);

  // Reset lines when the payment type changes so stale fields from a
  // previous type (e.g. carrier_id after switching to Bonus) don't
  // silently persist.
  useEffect(() => {
    setLines((prev) => prev.map((l) => ({ ...emptyLine(), amount: l.amount, effective_date: l.effective_date })));
  }, [header.payment_type]);

  // Reset the whole form on open so re-opening the dialog after a save
  // (or a cancel) doesn't inherit stale values.
  useEffect(() => {
    if (!open) return;
    setHeader({
      recipient_type: 'agent', recipient_id: '',
      payment_type: 'commission',
      period_month: now.getMonth() + 1, period_year: now.getFullYear(),
      notes: '',
    });
    setLines([emptyLine()]);
  }, [open]);

  const total = useMemo(() =>
    lines.reduce((s, l) => s + (parseFloat(l.amount) || 0), 0), [lines]);

  const canSave = !!header.recipient_id
    && lines.length > 0
    && lines.every((l) => parseFloat(l.amount) > 0
      && (!typeMeta.needsCarrier || l.carrier_id)
      && (typeMeta.needsCarrier ? true : (l.description || '').trim().length > 0));

  const monthOptions = Array.from({ length: 12 }, (_, i) => ({
    value: i + 1, label: new Date(2000, i, 1).toLocaleString('en-US', { month: 'long' }),
  }));
  const yearOptions = Array.from({ length: 4 }, (_, i) => now.getFullYear() - 1 + i);

  const handleSave = async () => {
    if (!canSave || saving) return;
    setSaving(true);
    try {
      const payload = {
        recipient_type: header.recipient_type,
        recipient_id: header.recipient_id,
        payment_type: header.payment_type,
        period_month: header.period_month,
        period_year: header.period_year,
        notes: (header.notes || '').trim() || null,
        lines: lines.map((l) => ({
          carrier_id: typeMeta.needsCarrier ? (l.carrier_id || null) : null,
          product: typeMeta.needsProduct ? (l.product || null) : null,
          sub_type: typeMeta.needsProduct ? (l.sub_type || null) : null,
          member_name: typeMeta.needsMember ? (l.member_name || null) : null,
          effective_date: l.effective_date || null,
          description: !typeMeta.needsCarrier ? (l.description || null) : null,
          amount: parseFloat(l.amount),
        })),
      };
      const { data } = await accountingAPI.createManualPayment(payload);
      toast.success(`Manual ${typeMeta.label} saved — ${data.line_count} line(s)`);
      onOpenChange(false);
      if (typeof onSaved === 'function') await onSaved();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to save manual payment');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl max-h-[92vh] overflow-y-auto" data-testid="manual-payment-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading">Add Manual Payment</DialogTitle>
          <DialogDescription>
            Record a payment that isn&apos;t in a carrier statement — bonus, reimbursement, correction, or a one-off commission.
          </DialogDescription>
        </DialogHeader>

        <form className="space-y-5 py-2" onSubmit={(e) => e.preventDefault()}>
          {/* ── Recipient ─────────────────────────────────────── */}
          <section className="space-y-3">
            <h3 className="text-xs font-semibold uppercase tracking-wider text-slate-500">Recipient</h3>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label>Type *</Label>
                <Select value={header.recipient_type}
                  onValueChange={(v) => setHeader((p) => ({ ...p, recipient_type: v, recipient_id: '' }))}
                >
                  <SelectTrigger data-testid="recipient-type-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {RECIPIENT_TYPES.map((r) => <SelectItem key={r.value} value={r.value}>{r.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5">
                <Label>{RECIPIENT_TYPES.find((r) => r.value === header.recipient_type)?.label} *</Label>
                <Select value={header.recipient_id}
                  onValueChange={(v) => setHeader((p) => ({ ...p, recipient_id: v }))}
                >
                  <SelectTrigger data-testid="recipient-select"><SelectValue placeholder="Search…" /></SelectTrigger>
                  <SelectContent>
                    {recipientOptions.map((o, i) => <SelectItem key={`${o.id}-${i}`} value={o.id}>{o.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>
          </section>

          <Separator />

          {/* ── Payment header ────────────────────────────────── */}
          <section className="space-y-3">
            <h3 className="text-xs font-semibold uppercase tracking-wider text-slate-500">Payment</h3>
            <div className="grid grid-cols-3 gap-3">
              <div className="space-y-1.5">
                <Label>Type *</Label>
                <Select value={header.payment_type}
                  onValueChange={(v) => setHeader((p) => ({ ...p, payment_type: v }))}
                >
                  <SelectTrigger data-testid="payment-type-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {PAYMENT_TYPES.map((t) => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5">
                <Label>Period Month</Label>
                <Select value={String(header.period_month)}
                  onValueChange={(v) => setHeader((p) => ({ ...p, period_month: parseInt(v, 10) }))}
                >
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {monthOptions.map((m) => <SelectItem key={m.value} value={String(m.value)}>{m.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5">
                <Label>Period Year</Label>
                <Select value={String(header.period_year)}
                  onValueChange={(v) => setHeader((p) => ({ ...p, period_year: parseInt(v, 10) }))}
                >
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {yearOptions.map((y) => <SelectItem key={y} value={String(y)}>{y}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>
            {!typeMeta.needsCarrier && (
              <div className="flex items-start gap-2 rounded-md bg-slate-50 border border-slate-200 p-2 text-xs text-slate-600">
                <Info size={13} className="mt-0.5 shrink-0" />
                <span>Carrier is not tracked for <strong>{typeMeta.label}</strong> payments — this statement will appear as <Badge variant="outline" className="mx-0.5 text-[10px]">N/A</Badge> in the Payments list.</span>
              </div>
            )}
          </section>

          <Separator />

          {/* ── Lines ─────────────────────────────────────────── */}
          <section className="space-y-3">
            <div className="flex items-center justify-between">
              <h3 className="text-xs font-semibold uppercase tracking-wider text-slate-500">Lines</h3>
              <Button type="button" size="sm" variant="outline" onClick={() => setLines((p) => [...p, emptyLine()])} data-testid="add-line-btn">
                <Plus size={12} className="mr-1" /> Add Line
              </Button>
            </div>
            <div className="space-y-2">
              {lines.map((line, i) => (
                <div key={i} className="rounded-lg border p-3 space-y-2" data-testid={`line-${i}`}>
                  <div className="grid grid-cols-12 gap-2">
                    {typeMeta.needsCarrier && (
                      <div className="col-span-4">
                        <Label className="text-xs">Carrier *</Label>
                        <Select value={line.carrier_id}
                          onValueChange={(v) => setLines((prev) => prev.map((l, idx) => idx === i ? { ...l, carrier_id: v } : l))}
                        >
                          <SelectTrigger><SelectValue placeholder="Select" /></SelectTrigger>
                          <SelectContent>
                            {carriers.map((c) => <SelectItem key={c.carrier_id} value={c.carrier_id}>{c.carrier_name}</SelectItem>)}
                          </SelectContent>
                        </Select>
                      </div>
                    )}
                    {typeMeta.needsProduct && (
                      <div className="col-span-3">
                        <Label className="text-xs">Product</Label>
                        <Input value={line.product}
                          onChange={(e) => setLines((prev) => prev.map((l, idx) => idx === i ? { ...l, product: e.target.value } : l))}
                          placeholder="e.g. MAPD"
                        />
                      </div>
                    )}
                    {typeMeta.needsProduct && (
                      <div className="col-span-2">
                        <Label className="text-xs">Sub-Type</Label>
                        <Select value={line.sub_type}
                          onValueChange={(v) => setLines((prev) => prev.map((l, idx) => idx === i ? { ...l, sub_type: v } : l))}
                        >
                          <SelectTrigger><SelectValue /></SelectTrigger>
                          <SelectContent>
                            <SelectItem value="New">New</SelectItem>
                            <SelectItem value="Renewal">Renewal</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                    )}
                    {typeMeta.needsMember && (
                      <div className="col-span-3">
                        <Label className="text-xs">Member Name</Label>
                        <Input value={line.member_name}
                          onChange={(e) => setLines((prev) => prev.map((l, idx) => idx === i ? { ...l, member_name: e.target.value } : l))}
                          placeholder="Optional"
                        />
                      </div>
                    )}
                    {!typeMeta.needsCarrier && (
                      <div className="col-span-9">
                        <Label className="text-xs">Description *</Label>
                        <Input value={line.description}
                          onChange={(e) => setLines((prev) => prev.map((l, idx) => idx === i ? { ...l, description: e.target.value } : l))}
                          placeholder={`Why this ${typeMeta.label.toLowerCase()} is owed`}
                        />
                      </div>
                    )}
                  </div>
                  <div className="grid grid-cols-12 gap-2 items-end">
                    <div className="col-span-4">
                      <Label className="text-xs">Effective Date</Label>
                      <Input type="date" value={line.effective_date}
                        onChange={(e) => setLines((prev) => prev.map((l, idx) => idx === i ? { ...l, effective_date: e.target.value } : l))}
                      />
                    </div>
                    <div className="col-span-4">
                      <Label className="text-xs">Amount *</Label>
                      <Input type="number" step="0.01" value={line.amount}
                        onChange={(e) => setLines((prev) => prev.map((l, idx) => idx === i ? { ...l, amount: e.target.value } : l))}
                        placeholder="0.00"
                        data-testid={`line-amount-${i}`}
                      />
                    </div>
                    <div className="col-span-4 flex justify-end">
                      <Button type="button" size="sm" variant="ghost" className="text-red-500"
                        onClick={() => setLines((p) => p.length > 1 ? p.filter((_, idx) => idx !== i) : p)}
                        disabled={lines.length === 1}
                        data-testid={`remove-line-${i}`}
                      >
                        <Trash2 size={13} className="mr-1" /> Remove
                      </Button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
            <div className="text-right text-sm">
              <span className="text-slate-500">Total: </span>
              <span className="font-semibold text-lg text-slate-900" data-testid="lines-total">
                ${total.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
              </span>
            </div>
          </section>

          <Separator />

          {/* ── Notes ─────────────────────────────────────────── */}
          <section className="space-y-1.5">
            <Label>Notes</Label>
            <Textarea rows={2} value={header.notes}
              onChange={(e) => setHeader((p) => ({ ...p, notes: e.target.value }))}
              placeholder="Internal reason / reference for this payment…"
            />
          </section>

          <DialogFooter className="mt-4">
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)} disabled={saving}>Cancel</Button>
            <Button
              type="button"
              disabled={!canSave || saving}
              onClick={handleSave}
              data-testid="save-manual-payment-btn"
            >
              {saving ? (
                <><Loader2 size={13} className="mr-1.5 animate-spin" /> Saving…</>
              ) : (
                <>Save Payment ({typeMeta.label})</>
              )}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default ManualPaymentDialog;
